import {
	IconButton,
	Link,
	mergeStyleSets,
	Stack,
	StackItem,
	TextField,
	Toggle,
} from '@fluentui/react';
import { observer } from 'mobx-react';
import { IEntityColumn } from '../../../../API';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EntityTypeEditorState } from '../EntityTypeEditor';
import { InfoTooltipLabel } from '../../../controls/InfoTooltipLabel';

const contentStyles = mergeStyleSets({
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: '5px',
	}
});

function EntityTypeConnectionInfo({
	state
}: {
	state: EntityTypeEditorState;
}) {
	const [listUrlErrorMessage, setListUrlErrorMessage] = useState<
		string | undefined
	>(
		state.SharePointListUrl
			? undefined
			: 'Please enter a valid SharePoint list URL.',
	);
	
	const timer = useRef<number>();
	const onListUrlChanged = useCallback(
		(_, val) => {
			state.setSharePointListUrl(val ?? '');
			if (!val) {
				setListUrlErrorMessage('Please enter a valid SharePoint list URL.');
				return;
			}
			setListUrlErrorMessage(undefined);
			window.clearTimeout(timer.current);
			timer.current = window.setTimeout(() => {
				state.loadList(val).then((listUrlColumns?: IEntityColumn[]) => {
					if (!listUrlColumns) {
						setListUrlErrorMessage('List could not be found.');
					}
				});
			}, 750);
		},
		[state],
	);

	useEffect(() => () => window.clearTimeout(timer.current), []);

	return (
		<section className={contentStyles.section}>
			
			<InfoTooltipLabel 
					label='SharePoint list URL'
					title={`Enter the full URL to the SharePoint List that contains your ${state.PluralName}.`}>

				<Stack horizontal>
					<StackItem grow>
						<TextField
							value={state.SharePointListUrl}
							onChange={onListUrlChanged}
							errorMessage={listUrlErrorMessage}
						/>			
					</StackItem>
					<StackItem>
						{state.SharePointListUrl && <IconButton 
							iconProps={{ iconName: 'Link' }}
							style={{cursor:'pointer'}}
							title='Open list in SharePoint'
							onClick={() => window.open(state.SharePointListUrl, '_blank')}
						/> }
					</StackItem>
				</Stack>
				
			</InfoTooltipLabel>


			<InfoTooltipLabel label='Available for template publishing' title={`When enabled, this allows user templates to be published for this entity type.`}>
				<Toggle style={{width: 48, height:24 }} color='green' checked={state.SharePointTemplateApprovals} onText="On" offText="Off" onChange={(e, checked) => { state.setSharePointTemplateApprovals(checked?.valueOf() ?? false); } } />
			</InfoTooltipLabel>

			{ state.SharePointTemplateApprovalsListUrl ? 
			<InfoTooltipLabel
				label='Template approvals list URL'
				title={`When template approve & publish is enabled, this points to the templates list.`}>

				<Link href={`${state.userConfig.tenantUrl}${state.SharePointTemplateApprovalsListUrl}`} target='_blank'>
					Open approvals list
				</Link>

			</InfoTooltipLabel>
			: <></> }

			<InfoTooltipLabel label='Workspace synchronisation' title={`When sync is enabled, changes to your SharePoint list will be automatically reflected on the workspace according to the item profile. A workspace will be automatically provisioned if necessary. `}>
				
				<Toggle style={{width: 48, height:24 }} color='green' checked={state.EnableWorkspaceSync} onText="On" offText="Off" onChange={(e, checked) => { state.setWorkspaceSync(checked?.valueOf() ?? false); } } />
			</InfoTooltipLabel>


		</section>
	);
}

export default observer(EntityTypeConnectionInfo);
